import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

import { useSearchStore } from "../../store/searchStore";
import { fetchListing, PropertyResult } from "../../services/api";
import ParcelLayer, { MIN_PARCEL_ZOOM } from "./ParcelLayer";
import styles from "./MapView.module.scss";

// Teton Valley center
const TETON_VALLEY_CENTER: L.LatLngExpression = [43.723034, -111.113678];
const DEFAULT_ZOOM = 11;

// --- Base map tile sources ------------------------------------------------
// listing-map.js (listing detail page) uses the same sources; keep them in step.
const OSM_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// Esri World Imagery: free, no API key, and from the same ArcGIS source as the
// county parcel overlay. The two Reference layers add roads and place names so
// the satellite view reads like a hybrid map rather than bare aerial imagery.
const ESRI_IMAGERY_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ESRI_ROADS_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}";
const ESRI_PLACES_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTRIBUTION =
  'Tiles &copy; <a href="https://www.esri.com">Esri</a>, Maxar, Earthstar Geographics, and the GIS User Community';

/** Compact price display: $1,200,000 → $1.2M. Unpriced listings read "TBD". */
export function formatPriceCompact(price: number): string {
  if (!price || price <= 0) return "TBD";
  if (price >= 1_000_000) {
    const m = price / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (price >= 1_000) return `$${Math.round(price / 1_000)}K`;
  return `$${price}`;
}

function escapeHtml(s: string): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}

/** Popup HTML for a marker */
function buildPopupHtml(p: PropertyResult): string {
  const address = escapeHtml(p.address);
  const img = p.image
    ? `<img src="${escapeHtml(p.image)}" style="width:100%;height:140px;object-fit:cover;display:block;" alt="${address}" />`
    : "";
  const details: string[] = [];
  if (p.beds > 0) details.push(`${p.beds} bd`);
  if (p.baths > 0) details.push(`${p.baths} ba`);
  if (p.sqft > 0) details.push(`${p.sqft.toLocaleString()} sqft`);
  if (p.acres > 0) details.push(`${p.acres} acres`);
  const price = p.price > 0 ? `$${p.price.toLocaleString()}` : "Price on request";
  const courtesy = p.listOffice
    ? `<div style="font-size:11px;color:rgba(255,255,255,0.5);margin:6px 0 0;line-height:1.3;">Listing courtesy of ${escapeHtml(p.listOffice)}</div>`
    : "";

  return `<a href="${escapeHtml(p.url)}" style="display:block;text-decoration:none;color:inherit;">
    ${img}
    <div style="padding:12px 14px;">
      <div style="font-weight:600;font-size:15px;color:#fff;margin:0 0 4px;letter-spacing:0.01em;">${price}</div>
      <div style="font-size:13px;color:rgba(255,255,255,0.75);margin:0 0 6px;line-height:1.35;">${address}<br/>${escapeHtml(p.city)}, ${escapeHtml(p.state)}</div>
      <div style="font-size:12px;color:rgba(255,255,255,0.5);">${details.join(" &middot; ")}</div>
      ${courtesy}
    </div>
  </a>`;
}

const POPUP_LOADING = `<div class="property-popup__note">Loading listing&hellip;</div>`;
const POPUP_UNAVAILABLE = `<div class="property-popup__note">Listing details are unavailable right now.</div>`;

// A listing with junk coordinates (0,0, wrong hemisphere) must not drag a fit
// off to another continent, so fits only count points inside a generous
// service-region box. Markers still plot everywhere.
const inServiceRegion = (lat: number, lng: number) =>
  lat > 41.5 && lat < 45.5 && lng > -114 && lng < -109;

/**
 * Plots EVERY listing matching the current filters (store.pins), independent
 * of how many list pages have loaded, so the map and the "N properties" count
 * agree. Popups load their listing details lazily: from the loaded list pages
 * when available, otherwise from the Listing endpoint.
 */
function MapPins() {
  const map = useMap();
  const pins = useSearchStore((s) => s.pins);
  const fitRequest = useSearchStore((s) => s.fitRequest);
  const hoveredId = useSearchStore((s) => s.hoveredPropertyId);
  const setHovered = useSearchStore((s) => s.setHoveredProperty);
  const setActive = useSearchStore((s) => s.setActiveProperty);

  const clusterRef = useRef<L.MarkerClusterGroup | null>(null);
  const markerMapRef = useRef<Map<string, L.Marker>>(new Map());
  const detailCache = useRef<Map<string, PropertyResult>>(new Map());

  // Fill a popup with listing details, preferring what the list already holds.
  const fillPopup = async (id: string, popup: L.Popup) => {
    const known =
      detailCache.current.get(id) ??
      (useSearchStore.getState().results as PropertyResult[]).find((r) => r.id === id);
    if (known) {
      popup.setContent(buildPopupHtml(known));
      return;
    }
    try {
      const listing = await fetchListing(id);
      if (listing) {
        detailCache.current.set(id, listing);
        popup.setContent(buildPopupHtml(listing));
      } else {
        popup.setContent(POPUP_UNAVAILABLE);
      }
    } catch {
      popup.setContent(POPUP_UNAVAILABLE);
    }
  };

  // Diff markers against the pin set: add new ids, drop vanished ones.
  useEffect(() => {
    if (!clusterRef.current) {
      clusterRef.current = L.markerClusterGroup({
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        chunkedLoading: true,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          let size = "small";
          if (count > 50) size = "large";
          else if (count > 20) size = "medium";
          return L.divIcon({
            html: `<div><span>${count}</span></div>`,
            className: `marker-cluster marker-cluster-${size}`,
            iconSize: L.point(40, 40),
          });
        },
      });
      map.addLayer(clusterRef.current);
    }

    const cluster = clusterRef.current;
    const markerMap = markerMapRef.current;
    const nextIds = new Set(pins.map((p) => p.id));

    const toRemove: L.Marker[] = [];
    markerMap.forEach((marker, id) => {
      if (!nextIds.has(id)) {
        toRemove.push(marker);
        markerMap.delete(id);
      }
    });
    if (toRemove.length) cluster.removeLayers(toRemove);

    const toAdd: L.Marker[] = [];
    pins.forEach((p) => {
      if (markerMap.has(p.id)) return;

      const icon = L.divIcon({
        className: "leaflet-price-pin",
        html: formatPriceCompact(p.price),
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });

      const marker = L.marker([p.lat, p.lng], { icon });
      marker.bindPopup(POPUP_LOADING, {
        className: "property-popup",
        maxWidth: 260,
        minWidth: 220,
        closeButton: true,
      });
      marker.on("popupopen", (e) => {
        void fillPopup(p.id, e.popup);
      });
      // Clicking a pin flags the listing so the list scrolls its card into view.
      marker.on("click", () => setActive(p.id));
      // Sync hover state with the list so hovering a pin highlights its card.
      marker.on("mouseover", () => setHovered(p.id));
      marker.on("mouseout", () => setHovered(null));

      markerMap.set(p.id, marker);
      toAdd.push(marker);
    });
    if (toAdd.length) cluster.addLayers(toAdd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, map, setHovered, setActive]);

  // Re-frame the map when the store asks for it (first load, AI query,
  // autocomplete pick). fitRequest only changes once the pins for that
  // search have landed, so this never frames a stale set. Flagged as a
  // programmatic move so the resulting moveend is swallowed and the list
  // stays on the whole result set.
  useEffect(() => {
    if (fitRequest === 0) return;
    const { fitFallback, setProgrammaticMove } = useSearchStore.getState();

    const bounds = L.latLngBounds([]);
    pins.forEach((p) => {
      if (inServiceRegion(p.lat, p.lng)) bounds.extend([p.lat, p.lng]);
    });
    // Nothing in the core region (e.g. a search for far-flung inventory):
    // frame whatever we do have rather than nothing.
    if (!bounds.isValid()) pins.forEach((p) => bounds.extend([p.lat, p.lng]));

    if (bounds.isValid()) {
      setProgrammaticMove(true);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    } else if (fitFallback) {
      setProgrammaticMove(true);
      map.flyTo([fitFallback.lat, fitFallback.lng], fitFallback.zoom, { duration: 1.1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitRequest, map]);

  // React to card hover from the list: highlight the matching pin, or the
  // cluster currently standing in for it, so it sits above neighbours.
  useEffect(() => {
    if (!hoveredId) return;
    const marker = markerMapRef.current.get(hoveredId);
    if (!marker) return;
    const visible = clusterRef.current?.getVisibleParent(marker) ?? marker;
    const el = visible.getElement();
    if (!el) return;
    el.classList.add("leaflet-price-pin--hovered");
    return () => el.classList.remove("leaflet-price-pin--hovered");
  }, [hoveredId]);

  return null;
}

/** A programmatic fit's moveend must arrive within this long to be ignored. */
const PROGRAMMATIC_MOVE_WINDOW_MS = 4000;

/** The map's current viewport, in the shape the search API takes. */
function viewBounds(map: L.Map) {
  const b = map.getBounds();
  return {
    north: b.getNorth(),
    south: b.getSouth(),
    east: b.getEast(),
    west: b.getWest(),
  };
}

/** The visitor grabbed the map: a programmatic move still flagged is no longer
 *  the one that will settle, so the next moveend must count as theirs. */
function releaseProgrammaticMove() {
  const st = useSearchStore.getState();
  if (st.programmaticMove) st.setProgrammaticMove(false);
}

/**
 * Keeps the list on the visible map area.
 *
 * A USER move (drag, wheel or pinch zoom, the zoom buttons, double-click,
 * shift-drag box zoom, keyboard, a cluster click) hands the new viewport to
 * followMap, which re-runs the list for it once the map settles. The pins are
 * not refetched: they always carry the whole filter set.
 *
 * A programmatic fit leaves the search unbounded: the map was just framed
 * around the full result set, so the two already agree, and the visitor never
 * sees an "in map area" scope they did not ask for.
 */
function MapBoundsTracker() {
  const followMap = useSearchStore((s) => s.followMap);
  const settleMapView = useSearchStore((s) => s.settleMapView);
  const setMapZoom = useSearchStore((s) => s.setMapZoom);
  // Set when the map container changes size (window resize, rotation).
  // Leaflet fires resize at once and the moveend it causes 200 ms later; a
  // real pan or zoom fires movestart first, which clears it.
  const resizedRef = useRef(false);

  const map = useMapEvents({
    // Leaflet nudges the map to keep a freshly opened popup on screen. That
    // is not the visitor asking for a new area, so flag it like a fit.
    autopanstart: () => useSearchStore.getState().setProgrammaticMove(true),
    // A drag is always the visitor, even one that interrupts a fit whose
    // moveend will now never arrive.
    dragstart: releaseProgrammaticMove,
    resize: () => {
      resizedRef.current = true;
    },
    movestart: () => {
      resizedRef.current = false;
    },
    moveend: (e) => {
      setMapZoom(e.target.getZoom());
      const bounds = viewBounds(e.target);
      const st = useSearchStore.getState();
      const resizeOnly = resizedRef.current;
      resizedRef.current = false;

      // Programmatic fit / fly: swallow the moveend it produces. The search
      // that caused the move already ran, unbounded. Time-boxed so a fit whose
      // animation never completed can't eat the visitor's next real pan.
      if (st.programmaticMove) {
        st.setProgrammaticMove(false);
        if (Date.now() - st.programmaticMoveAt < PROGRAMMATIC_MOVE_WINDOW_MS) {
          settleMapView(bounds);
          return;
        }
      }

      // A window resize is not the visitor picking an area: an unbounded list
      // stays unbounded. An area list follows the new edges.
      if (resizeOnly && !st.mapBounds) {
        settleMapView(bounds);
        return;
      }

      // User moved the map: the list follows once it settles. (The initial
      // search is kicked off by Results on mount, never from here.)
      followMap(bounds);
    },
  });

  useEffect(() => {
    // The opening view is the first one the whole-set list lines up with, so
    // a nudge right after load is measured against it.
    settleMapView(viewBounds(map));

    // A wheel zoom that interrupts a flyTo lands inside the programmatic
    // window and would be swallowed, so treat the wheel like a drag. Leaflet
    // has no map event for it, hence the DOM listener. Touch is left alone: a
    // tap during a popup autopan would un-swallow the autopan.
    const container = map.getContainer();
    container.addEventListener("wheel", releaseProgrammaticMove, { passive: true });
    return () => container.removeEventListener("wheel", releaseProgrammaticMove);
  }, [map, settleMapView]);

  return null;
}

/**
 * Leaflet caches its container size, so a map whose box changes under it
 * (mobile browser chrome, rotation, the list/map switch below xl) draws grey
 * gutters and anchors pins to stale pixels until told to re-measure.
 * listing-map.js does the same with a ResizeObserver.
 */
function MapInvalidateSize() {
  const map = useMap();
  const activeView = useSearchStore((s) => s.activeView);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);

  // Coming back to the map pane: re-measure once the pane is visible again.
  useEffect(() => {
    if (activeView !== "map") return;
    const frame = requestAnimationFrame(() => map.invalidateSize());
    return () => cancelAnimationFrame(frame);
  }, [activeView, map]);

  return null;
}

export default function MapView() {
  const isLoadingPins = useSearchStore((s) => s.isLoadingPins);
  const showParcels = useSearchStore((s) => s.showParcels);
  const toggleParcels = useSearchStore((s) => s.toggleParcels);
  const basemap = useSearchStore((s) => s.basemap);
  const toggleBasemap = useSearchStore((s) => s.toggleBasemap);
  const mapZoom = useSearchStore((s) => s.mapZoom);
  const satellite = basemap === "satellite";

  const parcelsNeedZoom = showParcels && mapZoom < MIN_PARCEL_ZOOM;

  return (
    <div className={styles.mapViewContainer}>
      <MapContainer
        center={TETON_VALLEY_CENTER}
        zoom={DEFAULT_ZOOM}
        className={styles.map}
        zoomControl={true}
        scrollWheelZoom={true}
      >
        {satellite ? (
          <>
            <TileLayer
              attribution={ESRI_ATTRIBUTION}
              url={ESRI_IMAGERY_URL}
              maxNativeZoom={19}
              maxZoom={22}
              zIndex={1}
            />
            {/* Transparent label overlays stacked above the imagery */}
            <TileLayer url={ESRI_ROADS_URL} maxNativeZoom={19} maxZoom={22} zIndex={2} />
            <TileLayer url={ESRI_PLACES_URL} maxNativeZoom={19} maxZoom={22} zIndex={3} />
          </>
        ) : (
          <TileLayer attribution={OSM_ATTRIBUTION} url={OSM_TILE_URL} zIndex={1} />
        )}
        <MapPins />
        <MapBoundsTracker />
        <MapInvalidateSize />
        <ParcelLayer visible={showParcels} satellite={satellite} />
      </MapContainer>

      {/* Top-right control row, clear of the zoom buttons (top left) and the
          attribution and mobile list/map switch (bottom). */}
      <div className={styles.mapControls}>
        {/* Base map style toggle. The label names the view you switch to,
            matching the familiar Google Maps pattern. */}
        <button
          type="button"
          className={styles.basemapToggle}
          onClick={toggleBasemap}
          title={satellite ? "Switch to map view" : "Switch to satellite view"}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.basemapToggleIcon}>
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.75" />
            <path
              d="M3 12h18M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
          <span>{satellite ? "Map" : "Satellite"}</span>
        </button>

        {/* Parcel / property-line overlay toggle */}
        <button
          type="button"
          className={`${styles.parcelToggle} ${showParcels ? styles.parcelToggleActive : ""}`}
          onClick={toggleParcels}
          aria-pressed={showParcels}
          title={showParcels ? "Hide parcel lines" : "Show parcel lines"}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.parcelToggleIcon}>
            <path
              d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinejoin="round"
            />
          </svg>
          <span>Parcel lines</span>
        </button>
      </div>

      {/* Parcels only render zoomed in: tell the user when they're too far out */}
      {parcelsNeedZoom && (
        <div className={styles.parcelZoomHint} role="status">
          Zoom in to see parcel lines
        </div>
      )}

      {/* Dims the map only while the pin set reloads. A pan refreshes just
          the list, so the map stays live under the visitor's hand. */}
      {isLoadingPins && (
        <div className={styles.mapLoadingOverlay}>
          <div className={styles.mapSpinner} />
        </div>
      )}
    </div>
  );
}
